import React, { useEffect, useRef, useState } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Textarea } from './textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { InternationalPhoneInput } from './international-phone-input';
import { CountrySelect } from './country-select';
import { Check, Edit, X } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions.ts';
import DynamicInput from './DynamicInput';
import { CustomAttributeDefinition, CustomAttributeFieldType } from '@/models/customAttribute';
import { ModulePermission, PermissionAction } from '@/types/permissions.ts';
import { getCountryByCode } from '@/data/countries';
import { isValidPhoneFormat, PHONE_VALIDATION_MESSAGE } from '@/utils/phoneValidation';

interface InlineEditProps {
    value: string | number | undefined;
    onSave: (value: string | number) => void;
    onCancel?: () => void;
    type?: 'text' | 'textarea' | 'select' | 'phone' | 'country';
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    selectOptions?: { value: string; label: string }[];
    multiline?: boolean;
    permissionModule?: ModulePermission;

    // Custom attribute support
    definition?: CustomAttributeDefinition;
    fieldType?: CustomAttributeFieldType;
    label?: string;
    isRequired?: boolean;
    options?: string[];
    validationRules?: Record<string, any>;
    onChange?: (value: any) => void;
    error?: string;
    onError?: (error: string) => void;
    /** Notified when inline edit mode starts or ends (e.g. to hide adjacent actions). */
    onEditingChange?: (isEditing: boolean) => void;
}

export const InlineEdit: React.FC<InlineEditProps> = ({
    value,
    onSave,
    onCancel,
    type = 'text',
    placeholder = 'Click to edit',
    className = '',
    disabled = false,
    selectOptions = [],
    multiline = false,
    permissionModule,

    // Custom attribute props
    definition,
    fieldType,
    label,
    isRequired,
    options,
    validationRules,
    onChange,
    error,
    onError,
    onEditingChange,
}) => {
    const { hasPermission } = usePermissions();
    const isEditable =
        !disabled && (!permissionModule || hasPermission(permissionModule, PermissionAction.EDIT));
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value?.toString() || '');
    const [phoneError, setPhoneError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // If custom attribute props are provided, use DynamicInput
    if (definition || fieldType) {
        return (
            <DynamicInput
                definition={definition}
                fieldType={fieldType}
                label={label}
                isRequired={isRequired}
                options={options}
                validationRules={validationRules}
                value={value}
                onChange={onChange || (newValue => onSave(newValue))}
                mode="inline"
                isEditing={isEditing}
                onEdit={() => {
                    setIsEditing(true);
                    onEditingChange?.(true);
                }}
                onSave={() => {
                    onSave(value);
                    setIsEditing(false);
                    onEditingChange?.(false);
                }}
                onCancel={() => {
                    setIsEditing(false);
                    onCancel?.();
                    onEditingChange?.(false);
                }}
                placeholder={placeholder}
                disabled={
                    disabled ||
                    !permissionModule ||
                    !hasPermission(permissionModule, PermissionAction.EDIT)
                }
                className={className}
                error={error}
                onError={onError}
            />
        );
    }

    useEffect(() => {
        if (isEditing) {
            if (type === 'textarea' && textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.select();
            } else if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.select();
            }
        }
    }, [isEditing, type]);

    const handleEdit = () => {
        if (!isEditable) return;
        setIsEditing(true);
        onEditingChange?.(true);
        setEditValue(value?.toString() || '');
        setPhoneError(null);
    };

    const handleSave = () => {
        if (type === 'phone' && editValue.trim() !== '' && !isValidPhoneFormat(editValue)) {
            setPhoneError(PHONE_VALIDATION_MESSAGE);
            return;
        }
        setPhoneError(null);
        const newValue = editValue;
        onSave(newValue);
        setIsEditing(false);
        onEditingChange?.(false);
    };

    const handleCancel = () => {
        setEditValue(value?.toString() || '');
        setPhoneError(null);
        setIsEditing(false);
        onEditingChange?.(false);
        onCancel?.();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancel();
        }
    };

    if (isEditing) {
        if (type === 'textarea') {
            return (
                <div className="space-y-2">
                    <div className="flex gap-2">
                        <Textarea
                            ref={textareaRef}
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={placeholder}
                            className={`${className} flex-1`}
                            rows={multiline ? 3 : 1}
                        />
                        <div className="flex gap-1">
                            <Button size="sm" onClick={handleSave} className="h-7 w-7 p-0">
                                <Check className="h-3 w-3" />
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancel}
                                className="h-7 w-7 p-0"
                            >
                                <X className="h-3 w-3" />
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        if (type === 'select') {
            return (
                <div className="space-y-2">
                    <div className="flex gap-2">
                        <Select value={editValue} onValueChange={setEditValue}>
                            <SelectTrigger className={`${className} flex-1`}>
                                <SelectValue placeholder={placeholder} />
                            </SelectTrigger>
                            <SelectContent>
                                {selectOptions.map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="flex gap-1">
                            <Button size="sm" onClick={handleSave} className="h-7 w-7 p-0">
                                <Check className="h-3 w-3" />
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancel}
                                className="h-7 w-7 p-0"
                            >
                                <X className="h-3 w-3" />
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        if (type === 'phone') {
            return (
                <div className="space-y-2">
                    <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-start">
                        <div className="min-w-0 flex-1" onKeyDown={handleKeyDown}>
                            <InternationalPhoneInput
                                value={editValue}
                                onChange={setEditValue}
                                placeholder={placeholder}
                                className="w-full min-w-0"
                                showValidation={false}
                            />
                        </div>
                        <div className="flex gap-1">
                            <Button size="sm" onClick={handleSave} className="h-7 w-7 p-0">
                                <Check className="h-3 w-3" />
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancel}
                                className="h-7 w-7 p-0"
                            >
                                <X className="h-3 w-3" />
                            </Button>
                        </div>
                    </div>
                    {phoneError && <p className="text-sm text-destructive">{phoneError}</p>}
                </div>
            );
        }

        if (type === 'country') {
            return (
                <div className="space-y-2">
                    <div className="flex gap-2">
                        <div className="flex-1" onKeyDown={handleKeyDown}>
                            <CountrySelect
                                value={editValue}
                                onChange={setEditValue}
                                placeholder={placeholder}
                                className="w-full"
                            />
                        </div>
                        <div className="flex gap-1">
                            <Button size="sm" onClick={handleSave} className="h-7 w-7 p-0">
                                <Check className="h-3 w-3" />
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancel}
                                className="h-7 w-7 p-0"
                            >
                                <X className="h-3 w-3" />
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-2">
                <div className="flex gap-2">
                    <Input
                        ref={inputRef}
                        type={type}
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        className={`${className} flex-1`}
                    />
                    <div className="flex gap-1">
                        <Button size="sm" onClick={handleSave} className="h-7 w-7 p-0">
                            <Check className="h-3 w-3" />
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleCancel}
                            className="h-7 w-7 p-0"
                        >
                            <X className="h-3 w-3" />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Get display value - for country and select types, show name/label instead of code/value
    const getDisplayValue = () => {
        if (type === 'country' && value) {
            const country = getCountryByCode(value.toString());
            return country?.name || value;
        }
        if (type === 'select' && value && selectOptions.length > 0) {
            const option = selectOptions.find(opt => opt.value === value.toString());
            return option?.label || value;
        }
        return value || placeholder;
    };

    return (
        <div
            className={`group ${isEditable ? 'cursor-pointer hover:bg-muted/50' : ''} rounded px-2 py-1 -mx-2 transition-colors ${className}`}
            onClick={isEditable ? handleEdit : undefined}
        >
            <div className="flex items-center justify-between min-w-0">
                <span className={`${!value ? 'text-muted-foreground' : ''} truncate flex-1 mr-2`}>
                    {getDisplayValue()}
                </span>
                {isEditable && <Edit className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
            </div>
        </div>
    );
};
